import { useState } from "react";
import { ChevronDown, ChevronRight, StickyNote } from "lucide-react";

import { AttachmentGroup } from "@/components/ui/attachment";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import NoteContent from "components/NoteContent";
import NoteSourceBadge from "components/NoteSourceBadge";
import NoteVersions from "components/NoteVersions";
import SavedAttachment from "components/SavedAttachment";

// Body of the note an audit entry is about, shown under the entry sentence.
// The entry is immutable, so a note deleted later keeps its body and files
// here; `attachments` is the entry's own list, which includes deleted files.
// The sentence already states the action, so only the category is surfaced;
// the note's own timestamp would duplicate the entry's.
export function NoteEntryBody({
  note,
  attachments,
  versions,
  collapsed = false,
}) {
  const [showBody, setShowBody] = useState(!collapsed);
  // A payload cached before either field existed marshals it as null rather
  // than omitting it, so a parameter default would not cover it.
  const files = attachments ?? [];
  // A deletion entry is a record that the note is gone, not somewhere to read
  // it, so its body starts folded away rather than filling the log.
  const disclosure = collapsed && (
    <Button
      variant="ghost"
      size="xs"
      className="tw:mt-2"
      aria-expanded={showBody}
      onClick={() => setShowBody(open => !open)}
    >
      {showBody ? <ChevronDown /> : <ChevronRight />}
      {showBody ? "Hide the deleted note" : "Show the deleted note"}
    </Button>
  );

  if (collapsed && !showBody) {
    return disclosure;
  }

  return (
    <>
      {disclosure}
      <div className="tw:mt-2 tw:rounded-md tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface)] tw:px-3 tw:py-2">
        <NoteContent note={note} className="tw:max-h-96 tw:overflow-y-auto" />
        {note.category_name && (
          <div className="tw:mt-1">
            <Badge variant="secondary" size="sm">
              {note.category_name}
            </Badge>
          </div>
        )}
        {files.length > 0 && (
          <AttachmentGroup className="tw:mt-2 tw:flex-wrap">
            {files.map(attachment => (
              <SavedAttachment
                key={attachment.id ?? attachment.url}
                attachment={attachment}
                size="xs"
              />
            ))}
          </AttachmentGroup>
        )}
        <NoteVersions versions={versions} />
      </div>
    </>
  );
}

/**
 * The sentence for a note entry, with the note's body beneath it. `collapsed`
 * folds the body away, for an entry that records a deletion.
 */
const noteMessage =
  (verb, { collapsed = false } = {}) =>
  ({
    user_name,
    model_id,
    model_type,
    source_name,
    note,
    note_attachments,
    note_versions,
  }) => (
    <>
      <p>
        {user_name} {verb} a note.{" "}
        <NoteSourceBadge
          sourceName={source_name}
          modelType={model_type}
          modelId={model_id}
        />
      </p>
      {note && (
        <NoteEntryBody
          note={note}
          attachments={note_attachments}
          versions={note_versions}
          collapsed={collapsed}
        />
      )}
    </>
  );

export const renderNoteCreatedMessage = noteMessage("added");

export const NOTE_EVENTS = {
  "activity_log.created": {
    title: "Note Created",
    icon: StickyNote,
    message: renderNoteCreatedMessage,
  },
  "activity_log.modified": {
    title: "Note Edited",
    icon: StickyNote,
    message: noteMessage("edited"),
  },
  "activity_log.deleted": {
    title: "Note Deleted",
    icon: StickyNote,
    message: noteMessage("deleted", { collapsed: true }),
  },
};
